import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpRequest, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl = this.resolveBaseUrl();
  private apiKey = environment.API_KEY;

  private http = inject(HttpClient);
  private authService = inject(AuthService);

  private resolveBaseUrl(): string {
    const configured = environment.apiUrl;
    if (typeof window === 'undefined') return configured;
    const host = window.location.hostname;
    const isLocalHost = host === 'localhost:3000' || host === '127.0.0.1:3000';
    if (!isLocalHost) return configured;
    if (configured.startsWith('http://') || configured.startsWith('https://')) {
      return '/api';
    }
    return configured;
  }

  getSseRequest(endpoint: string): { url: string; headers: Record<string, string> } {
    return {
      url: `${this.baseUrl}/${endpoint}`,
      headers: {
        ...this.buildHeaders(false),
        Accept: 'text/event-stream'
      }
    };
  }

  private normalizeBody<T>(body: unknown, httpStatus: number): T {
    if (body && typeof body === 'object') {
      const base = body as Record<string, unknown>;
      const output: Record<string, unknown> = { ...base, statusCode: httpStatus };
      return output as T;
    }
    return body as T;
  }

  private buildHeaders(includeContentType = false): Record<string, string> {
    const headers: Record<string, string> = {
      'X-API-Key': this.apiKey,
      'Accept': 'application/json'
    };

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    const token = this.authService.getToken();
    if (token) {
      // Keep existing header for backward compatibility
      headers['admaxhub-key'] = token;
      // Add standard Authorization header per API spec
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  get<T>(endpoint: string, params?: { [param: string]: string | number | boolean | readonly (string | number | boolean)[] }): Observable<T> {
    const headers = this.buildHeaders();
    return this.http.get<unknown>(`${this.baseUrl}/${endpoint}`, { params, headers, observe: 'response' })
      .pipe(map(res => this.normalizeBody<T>(res.body, res.status)));
  }

  getBlob(endpoint: string, params?: { [param: string]: string | number | boolean | readonly (string | number | boolean)[] }): Observable<Blob> {
    const headers = {
      ...this.buildHeaders(false),
      Accept: 'text/csv,application/octet-stream,application/json'
    };
    return this.http.get(`${this.baseUrl}/${endpoint}`, { params, headers, responseType: 'blob' });
  }

  post<T>(endpoint: string, body: unknown): Observable<T> {
    const headers = this.buildHeaders(true);
    return this.http.post<unknown>(`${this.baseUrl}/${endpoint}`, body, { headers, observe: 'response' })
      .pipe(map(res => this.normalizeBody<T>(res.body, res.status)));
  }

  put<T>(endpoint: string, body: unknown): Observable<T> {
    const headers = this.buildHeaders(true);
    return this.http.put<unknown>(`${this.baseUrl}/${endpoint}`, body, { headers, observe: 'response' })
      .pipe(map(res => this.normalizeBody<T>(res.body, res.status)));
  }

  // Add multipart/form-data PUT support
  putFormData<T>(endpoint: string, formData: FormData): Observable<T> {
    const headers = this.buildHeaders(false);
    return this.http.put<unknown>(`${this.baseUrl}/${endpoint}`, formData, { headers, observe: 'response' })
      .pipe(map(res => this.normalizeBody<T>(res.body, res.status)));
  }

  // Multipart PUT with progress events
  putFormDataWithProgress(endpoint: string, formData: FormData) {
    const headers = new HttpHeaders(this.buildHeaders(false));
    const req = new HttpRequest('PUT', `${this.baseUrl}/${endpoint}`, formData, { headers, reportProgress: true });
    return this.http.request(req);
  }

  patch<T>(endpoint: string, body: unknown): Observable<T> {
    const headers = this.buildHeaders(true);
    return this.http.patch<unknown>(`${this.baseUrl}/${endpoint}`, body, { headers, observe: 'response' })
      .pipe(map(res => this.normalizeBody<T>(res.body, res.status)));
  }

  // Add multipart/form-data PATCH support (no Content-Type so browser sets boundary)
  patchFormData<T>(endpoint: string, formData: FormData): Observable<T> {
    const headers = this.buildHeaders(false);
    return this.http.patch<unknown>(`${this.baseUrl}/${endpoint}`, formData, { headers, observe: 'response' })
      .pipe(map(res => this.normalizeBody<T>(res.body, res.status)));
  }

  // Multipart PATCH with progress events
  patchFormDataWithProgress(endpoint: string, formData: FormData) {
    const headers = new HttpHeaders(this.buildHeaders(false));
    const req = new HttpRequest('PATCH', `${this.baseUrl}/${endpoint}`, formData, { headers, reportProgress: true });
    return this.http.request(req);
  }

  // PUT directly to a signed URL (e.g., GCS) using native fetch to avoid Angular HttpClient CORS preflight issues
  putToSignedUrl(signedUrl: string, file: File | Blob, contentType: string): Observable<{ type: number; loaded: number; total: number }> {
    return new Observable(subscriber => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signedUrl, true);
      xhr.setRequestHeader('Content-Type', contentType);

      xhr.upload.onprogress = (event) => {
        subscriber.next({
          type: 1, // HttpEventType.UploadProgress
          loaded: event.loaded,
          total: event.total,
        });
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          subscriber.next({ type: 4, loaded: xhr.response?.size || 0, total: xhr.response?.size || 0 });
          subscriber.complete();
        } else {
          subscriber.error(new Error(`GCS upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        subscriber.error(new Error('GCS upload failed: network error'));
      };

      xhr.send(file);

      return () => xhr.abort();
    });
  }

  delete<T>(endpoint: string, body?: unknown): Observable<T> {
    const headers = this.buildHeaders(true);
    const url = `${this.baseUrl}/${endpoint}`;
    if (body !== undefined) {
      return this.http.request<unknown>('DELETE', url, { headers, body, observe: 'response' })
        .pipe(map(res => this.normalizeBody<T>(res.body, res.status)));
    }
    return this.http.delete<unknown>(url, { headers, observe: 'response' })
      .pipe(map(res => this.normalizeBody<T>(res.body, res.status)));
  }
}
